import type { ScopeKind } from "../../shared/rbac/permissions";
import {
  ALLOW_ALL_ROLE_IDS,
  BUILTIN_ROLES,
  type BuiltinRoleId,
} from "../../shared/rbac/roles";
import type { IconName } from "../assets/icons";
import { SCOPE_ICONS } from "../components/Settings/RolesSettings/constants";

function scopeIcon(scopeKind: string): IconName | undefined {
  return SCOPE_ICONS[scopeKind as ScopeKind];
}

const SCOPE_SINGULAR: Record<string, string> = {
  space: "space",
  connected_app: "connector",
  user_group: "group",
  skill: "skill",
  network: "network",
};

export function hiddenScopeLabel(count: number, scopeKind: string): string {
  const noun = SCOPE_SINGULAR[scopeKind] ?? scopeKind;
  const plural = count === 1 ? noun : `${noun}s`;
  return `${count} ${plural} you don’t have access to`;
}

export type RoleChip = {
  key: string;
  icon?: IconName;
  label: string;
  fullLabel: string;
  scopeKind: string;
};

export type GroupedRoleChip = {
  key: string;
  icon?: IconName;
  label: string;
  count: number;
  details: string[];
  scopeKind: string;
};

type GrantLike = {
  scopeId?: string;
  builtinRoleId?: string | null;
  customRole?: {
    id: string;
    name: string;
    scopeKind: string;
  } | null;
};

export type ResolveScopeName = (
  scopeId: string,
  scopeKind: string
) => string | null | undefined;

export function grantToRoleChip(grant: GrantLike): RoleChip | null {
  if (grant.builtinRoleId) {
    const role = BUILTIN_ROLES[grant.builtinRoleId as BuiltinRoleId];
    if (!role) return null;
    return {
      key: `b:${grant.builtinRoleId}`,
      icon: scopeIcon(role.scopeKind) ?? "id-card-lanyard",
      label: role.label,
      fullLabel: role.label,
      scopeKind: role.scopeKind,
    };
  }
  if (grant.customRole) {
    return {
      key: `c:${grant.customRole.id}`,
      icon: scopeIcon(grant.customRole.scopeKind) ?? "id-card-lanyard",
      label: grant.customRole.name,
      fullLabel: grant.customRole.name,
      scopeKind: grant.customRole.scopeKind,
    };
  }
  return null;
}

function detailForGrant(
  grant: GrantLike,
  chip: RoleChip,
  resolveName?: ResolveScopeName
): string | null {
  if (chip.scopeKind === "tenant") return chip.fullLabel;
  if (!resolveName || !grant.scopeId) return null;
  return resolveName(grant.scopeId, chip.scopeKind) ?? null;
}

export function buildResolveScopeName(
  spaces: readonly { id: string; name: string }[],
  apps: readonly { id: string; name: string }[],
  groups: readonly { id: string; name: string }[],
  skills: readonly { id: string; name: string }[] = [],
  networks: readonly { id: string; name: string }[] = [],
  serviceAccounts: readonly { id: string; name: string }[] = []
): ResolveScopeName {
  const spaceById = new Map(spaces.map((s) => [s.id, s.name]));
  const appById = new Map(apps.map((a) => [a.id, a.name]));
  const groupById = new Map(groups.map((g) => [g.id, g.name]));
  const skillById = new Map(skills.map((s) => [s.id, s.name]));
  const networkById = new Map(networks.map((n) => [n.id, n.name]));
  const serviceAccountById = new Map(
    serviceAccounts.map((s) => [s.id, s.name])
  );
  return (scopeId, scopeKind) => {
    if (scopeKind === "tenant") return "Tenant";
    if (scopeKind === "space") return spaceById.get(scopeId) ?? null;
    if (scopeKind === "connected_app") return appById.get(scopeId) ?? null;
    if (scopeKind === "skill") return skillById.get(scopeId) ?? null;
    if (scopeKind === "network") return networkById.get(scopeId) ?? null;
    if (scopeKind === "user_group") return groupById.get(scopeId) ?? null;
    if (scopeKind === "service_account")
      return serviceAccountById.get(scopeId) ?? null;
    return null;
  };
}

export function roleIdFromChipKey(key: string): string {
  if (key === "admin") return "tenant_admin";
  return key.slice(2);
}

export function collectRoleChips(
  grants: readonly GrantLike[],
  resolveName?: ResolveScopeName
): GroupedRoleChip[] {
  const allowAllIds = new Set<string>(ALLOW_ALL_ROLE_IDS);
  const byRole = new Map<string, GroupedRoleChip>();
  const hiddenByRole = new Map<string, number>();
  const seenScopeByRole = new Map<string, Set<string>>();
  const tenantRoles = new Map<string, string>();
  let hasAdmin = false;
  for (const gr of grants) {
    const chip = grantToRoleChip(gr);
    if (!chip) continue;
    if (chip.scopeKind === "tenant") {
      if (gr.builtinRoleId && allowAllIds.has(gr.builtinRoleId)) {
        hasAdmin = true;
      } else if (!tenantRoles.has(chip.key)) {
        tenantRoles.set(chip.key, chip.fullLabel);
      }
      continue;
    }
    const scopeKey = gr.scopeId ?? "";
    let seen = seenScopeByRole.get(chip.key);
    if (!seen) {
      seen = new Set();
      seenScopeByRole.set(chip.key, seen);
    }
    if (seen.has(scopeKey)) continue;
    seen.add(scopeKey);

    const detail = detailForGrant(gr, chip, resolveName);
    if (detail === null) {
      hiddenByRole.set(chip.key, (hiddenByRole.get(chip.key) ?? 0) + 1);
    }

    const existing = byRole.get(chip.key);
    if (existing) {
      existing.count++;
      if (detail && !existing.details.includes(detail))
        existing.details.push(detail);
    } else {
      byRole.set(chip.key, {
        key: chip.key,
        icon: chip.icon,
        label: chip.label,
        count: 1,
        details: detail ? [detail] : [],
        scopeKind: chip.scopeKind,
      });
    }
  }

  for (const [key, hidden] of hiddenByRole) {
    const chip = byRole.get(key);
    if (chip) {
      chip.details.push(hiddenScopeLabel(hidden, chip.scopeKind));
    }
  }

  const result = [...byRole.values()];
  if (hasAdmin) {
    result.push({
      key: "admin",
      icon: "crown",
      label: "Admin",
      count: 1,
      details: ["Full access"],
      scopeKind: "tenant",
    });
  }
  if (tenantRoles.size > 0) {
    result.push({
      key: "tenant",
      icon: scopeIcon("tenant"),
      label: "Tenant",
      count: tenantRoles.size,
      details: [...tenantRoles.values()],
      scopeKind: "tenant",
    });
  }
  return result;
}
